/**
 * VB Server API Client
 *
 * Base client for all vb-server API calls.
 * Handles JWT auth, error handling, and response unwrapping.
 *
 * @example
 * import { serverFetch, serverGet, serverPost } from './client';
 * const channels = await serverGet<Channel[]>(`/orgs/${orgId}/channels`);
 */

import { logger } from '../../utils/logger';
import { getToken, refreshToken as refreshV2Token } from '../../v2/services/auth';

// =============================================================================
// Configuration
// =============================================================================

/**
 * Get vb-server base URL from environment
 * Uses /v1 API version by default
 *
 * In development, returns empty string to use Vite proxy (same-origin cookies)
 * In production, uses REACT_APP_API_URL (REQUIRED)
 */
export function getServerUrl(): string {
  // In dev, use relative URLs to go through Vite proxy (same-origin cookies)
  if (import.meta.env.DEV) {
    return '/v1';
  }

  // Production: Use REACT_APP_API_URL but replace /v3 with /v1 for vb-server
  const baseUrl = import.meta.env.REACT_APP_API_URL;
  if (!baseUrl) {
    throw new Error('REACT_APP_API_URL environment variable is required');
  }
  // If it ends with /v3, replace with /v1
  if (baseUrl.endsWith('/v3')) {
    return baseUrl.replace('/v3', '/v1');
  }
  // Otherwise assume it's already the v1 URL or needs /v1 appended
  return baseUrl.endsWith('/v1') ? baseUrl : `${baseUrl}/v1`;
}

/**
 * Get current JWT token for authentication
 */
export function getJWT(): string | undefined {
  return getToken() || undefined;
}

// =============================================================================
// Error Handling
// =============================================================================

/**
 * API Error with typed response data
 */
export class APIError extends Error {
  public readonly status: number;
  public readonly statusText: string;
  public readonly data: unknown;
  public readonly requestId?: string;

  constructor(status: number, statusText: string, data: unknown, requestId?: string) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'APIError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.requestId = requestId;
  }

  /**
   * Get error message from response data. Prefers the first field-level
   * validation message from `details` (when the server returned a 400 with
   * structured validation errors) over the generic top-level `error`.
   */
  get errorMessage(): string {
    if (typeof this.data === 'object' && this.data !== null) {
      const d = this.data as Record<string, unknown>;
      const details = d.details;
      if (Array.isArray(details) && details.length > 0) {
        const first = details[0];
        if (first && typeof first === 'object') {
          const msg = (first as Record<string, unknown>).message;
          if (typeof msg === 'string' && msg.length > 0) return msg;
        }
      }
      return (d.error as string) || (d.message as string) || this.statusText;
    }
    return this.statusText;
  }
}

export class RateLimitError extends APIError {
  public readonly retryAfter: number;

  constructor(retryAfter: number, data: unknown, requestId?: string) {
    super(429, 'Too Many Requests', data, requestId);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

/** Shared user-facing copy for a rate-limited request (used across features). */
export const RATE_LIMIT_MESSAGE = 'Too many requests — please wait a moment and try again.';

/**
 * Maps an API error to a concise, user-facing message for inline display.
 * Centralizes rate-limit copy so Review, Live, and providers stay consistent
 * instead of each catch block duplicating the strings.
 */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof RateLimitError) {
    return RATE_LIMIT_MESSAGE;
  }
  return fallback;
}

// =============================================================================
// Response Types
// =============================================================================

/**
 * Standard vb-server API response wrapper
 */
export interface APIResponse<T> {
  success: boolean;
  data: T;
  total?: number;
  hasMore?: boolean;
  pagination?: {
    limit: number;
    before?: string | null;
    after?: string | null;
  };
  requestId?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function throwIfRateLimited(response: Response, data: unknown, requestId?: string): void {
  if (response.status !== 429) return;
  const bodyRaw =
    typeof data === 'object' && data !== null
      ? (data as Record<string, unknown>).retryAfter
      : undefined;
  const headerRaw = Number(response.headers.get('Retry-After'));
  const candidate =
    typeof bodyRaw === 'number' && bodyRaw > 0
      ? bodyRaw
      : Number.isFinite(headerRaw) && headerRaw > 0
        ? headerRaw
        : 60;
  // Cap so a buggy/hostile server can't freeze the UI for hours.
  const retryAfter = Math.min(candidate, 300);
  throw new RateLimitError(retryAfter, data, requestId);
}

// =============================================================================
// Core Fetch Function
// =============================================================================

/**
 * Make an authenticated request to vb-server
 *
 * @param path - API path (without base URL, e.g., '/orgs/123/channels')
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns Unwrapped response data
 * @throws APIError on non-2xx responses
 *
 * @example
 * // GET request
 * const channels = await serverFetch<Channel[]>('/orgs/123/channels');
 *
 * // POST request
 * const channel = await serverFetch<Channel>('/orgs/123/channels', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'New Channel' })
 * });
 */
export async function serverFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const baseUrl = getServerUrl();
  const url = `${baseUrl}${path}`;

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((options.headers as Record<string, string>) || {})
  };

  // Add Authorization header if we have a real token (not cookie placeholder)
  const token = getJWT();
  if (token && token !== 'cookie-auth') {
    headers.Authorization = `Bearer ${token}`;
  }

  // Add API key if enabled
  const apiKey = import.meta.env.REACT_APP_API_KEY;
  if (apiKey && import.meta.env.REACT_APP_ENABLE_API_KEY_AUTH === 'true') {
    headers['X-API-Key'] = apiKey;
  }

  // Make request - use credentials: 'include' for cookie auth
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include' // Send cookies for auth
  });

  // Get request ID from response headers
  const requestId = response.headers.get('X-Request-ID') || undefined;

  // Handle errors
  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = { error: response.statusText };
    }

    // Handle 401 - try to refresh token once
    if (response.status === 401 && getToken()) {
      logger.info('[api/server] Token expired, attempting refresh');
      const refreshed = await refreshV2Token();
      if (refreshed) {
        // Retry the request - cookies will be updated by the refresh
        const retryResponse = await fetch(url, {
          ...options,
          headers,
          credentials: 'include'
        });

        if (retryResponse.ok) {
          const retryJson = await retryResponse.json();
          if (retryJson.success !== undefined && retryJson.data !== undefined) {
            return retryJson.data;
          }
          return retryJson as unknown as T;
        }
      }
    }

    throwIfRateLimited(response, data, requestId);

    logger.error('[api/server] Request failed:', {
      url,
      status: response.status,
      data,
      requestId
    });
    throw new APIError(response.status, response.statusText, data, requestId);
  }

  // Parse response
  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType.includes('application/json')) {
    // Return empty for non-JSON responses (e.g., DELETE with no body)
    return {} as T;
  }

  const json = (await response.json()) as APIResponse<T>;

  // Unwrap { success, data } wrapper if present
  if (json.success !== undefined && json.data !== undefined) {
    return json.data;
  }

  // Return as-is if no wrapper
  return json as unknown as T;
}

// =============================================================================
// Convenience Methods
// =============================================================================

/**
 * GET request to vb-server (returns unwrapped data)
 *
 * Use for simple collection/entity fetches where you just need the data:
 * - listChannels, listAlerts, listTags, listSpeakers
 * - getChannel, getAlert, getMessage (single entity)
 */
export async function serverGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return serverFetch<T>(path, { method: 'GET', signal });
}

/**
 * GET request to vb-server - returns full APIResponse (no unwrapping)
 *
 * Use for paginated endpoints where you need metadata:
 * - listMessages, searchMessages (need total, hasMore, pagination)
 *
 * @example
 * const response = await serverGetFull<Message[]>('/messages?...');
 * console.log(response.data);      // Message[]
 * console.log(response.total);     // number
 * console.log(response.hasMore);   // boolean
 * console.log(response.pagination); // { limit, before?, after? }
 */
export async function serverGetFull<T>(
  path: string,
  signal?: AbortSignal
): Promise<APIResponse<T>> {
  const baseUrl = getServerUrl();
  const url = `${baseUrl}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  // Add Authorization header if we have a real token
  const token = getJWT();
  if (token && token !== 'cookie-auth') {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include', // Send cookies for auth
    signal
  });

  const requestId = response.headers.get('X-Request-ID') || undefined;

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = { error: response.statusText };
    }
    throwIfRateLimited(response, data, requestId);

    logger.error('[api/server] Request failed:', { url, status: response.status, data, requestId });
    throw new APIError(response.status, response.statusText, data, requestId);
  }

  const json = (await response.json()) as APIResponse<T>;
  return { ...json, requestId };
}

/**
 * POST request to vb-server
 */
export async function serverPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return serverFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    signal
  });
}

/**
 * PUT request to vb-server
 */
export async function serverPut<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return serverFetch<T>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
    signal
  });
}

/**
 * PATCH request to vb-server
 */
export async function serverPatch<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<T> {
  return serverFetch<T>(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
    signal
  });
}

/**
 * DELETE request to vb-server
 */
export async function serverDelete<T = void>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  return serverFetch<T>(path, {
    method: 'DELETE',
    body: body ? JSON.stringify(body) : undefined,
    signal
  });
}

/**
 * POST request that returns a raw Response (for binary/blob downloads).
 * Uses the same auth logic as serverFetch but does not parse the body.
 */
export async function serverPostRaw(path: string, body: unknown): Promise<Response> {
  const baseUrl = getServerUrl();
  const url = `${baseUrl}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  const token = getJWT();
  if (token && token !== 'cookie-auth') {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = { error: response.statusText };
    }
    const requestId = response.headers.get('X-Request-ID') || undefined;
    throwIfRateLimited(response, data, requestId);
    throw new APIError(response.status, response.statusText, data, requestId);
  }

  return response;
}

/**
 * Wait for JWT to be available (with retry)
 * Useful for initial page loads where auth may still be initializing
 */
export async function waitForJWT(maxAttempts = 10, delayMs = 1000): Promise<string> {
  let attempts = 0;
  while (attempts < maxAttempts) {
    const jwt = getJWT();
    if (jwt) return jwt;
    attempts++;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  throw new APIError(401, 'Unauthorized', { error: 'Failed to get JWT token after retries' });
}
